// src/components/ManagementDashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";

import PlotManagement from "./PlotManagement"; // (you can rename later)
import Users from "./Users";
import Bookings from "./Bookings";
import Report from "./Report";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faTree,
  faUsers,
  faFileInvoice,
  faChartBar,
  faRotateRight,
  faHouse,
  faCircleCheck,
  faHand,
} from "@fortawesome/free-solid-svg-icons";

import { collection, getDocs, query, orderBy } from "firebase/firestore";
import "./ManagementDashboard.css";
import logo from "./../assets/logo.png";

const VILLA_COLLECTION = "ClarksExoticaVilla";

const safeNum = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatNum = (num) => {
  try {
    return new Intl.NumberFormat("en-IN").format(safeNum(num));
  } catch {
    return safeNum(num).toLocaleString();
  }
};

const ManagementDashboard = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [error, setError] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [dashboardStats, setDashboardStats] = useState({
    totalVillas: 0,
    available: 0,
    booked: 0,
    sold: 0,
    blocked: 0,

    totalBuiltUpSqft: 0,
    availableBuiltUpSqft: 0,
    bookedBuiltUpSqft: 0,
    soldBuiltUpSqft: 0,
    blockedBuiltUpSqft: 0,

    totalLandSqft: 0,
    availableLandSqft: 0,
    bookedLandSqft: 0,
    soldLandSqft: 0,
    blockedLandSqft: 0,

    configCounts: {},
    facingCounts: {},

    projectName: "Clarks Exotica Villa",
    lastUpdated: null,
  });

  const navigate = useNavigate();

  // Auto clear error
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 3000);
    return () => clearTimeout(t);
  }, [error]);

  const fetchVillaData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const villasRef = collection(db, VILLA_COLLECTION);

      // Ordering by villaId is safe like "villa-1"
      const q = query(villasRef, orderBy("villaId", "asc"));

      const snap = await getDocs(q);
      const villas = snap.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));

      const totalVillas = villas.length;

      const countByStatus = (st) => villas.filter((v) => v.Status === st).length;

      const available = countByStatus("Available");
      const booked = countByStatus("Booked");
      const sold = countByStatus("Sold");
      const blocked = countByStatus("Soft-Block") + countByStatus("Blocked");

      const sumBuiltUp = (filterStatus = null) =>
        villas
          .filter((v) => (filterStatus ? v.Status === filterStatus : true))
          .reduce((sum, v) => sum + safeNum(v.builtUpAreaSqft), 0);

      const sumLand = (filterStatus = null) =>
        villas
          .filter((v) => (filterStatus ? v.Status === filterStatus : true))
          .reduce((sum, v) => sum + safeNum(v.LandAreaSqft), 0);

      // Quick insights
      const configCounts = villas.reduce((acc, v) => {
        const key = (v.configuration || "Unknown").toString().trim();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      const facingCounts = villas.reduce((acc, v) => {
        const key = (v.facing || "Unknown").toString().trim();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      setDashboardStats({
        totalVillas,
        available,
        booked,
        sold,
        blocked,

        totalBuiltUpSqft: sumBuiltUp(),
        availableBuiltUpSqft: sumBuiltUp("Available"),
        bookedBuiltUpSqft: sumBuiltUp("Booked"),
        soldBuiltUpSqft: sumBuiltUp("Sold"),
        blockedBuiltUpSqft: sumBuiltUp("Soft-Block"),

        totalLandSqft: sumLand(),
        availableLandSqft: sumLand("Available"),
        bookedLandSqft: sumLand("Booked"),
        soldLandSqft: sumLand("Sold"),
        blockedLandSqft: sumLand("Soft-Block"),

        configCounts,
        facingCounts,

        projectName: "Clarks Exotica Villa",
        lastUpdated: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error fetching villa dashboard data:", err);
      setError("Failed to load villa dashboard data. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Auth guard + initial load
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) navigate("/login");
      else fetchVillaData();
    });
    return () => unsub();
  }, [navigate, fetchVillaData]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
      setError("Failed to log out. Please try again.");
    } finally {
      setIsLoggingOut(false);
    }
  }, [navigate]);

  const handleViewMap = useCallback(() => {
    window.open("/", "_blank");
  }, []);

  // UI helpers
  const total = dashboardStats.totalVillas || 1;
  const soldPct = Math.round((dashboardStats.sold / total) * 100);
  const bookedPct = Math.round((dashboardStats.booked / total) * 100);
  const blockedPct = Math.round((dashboardStats.blocked / total) * 100);

  const topConfigs = Object.entries(dashboardStats.configCounts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  const topFacings = Object.entries(dashboardStats.facingCounts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  const builtUpVsLandRatio =
    dashboardStats.totalLandSqft > 0
      ? Math.round((dashboardStats.totalBuiltUpSqft / dashboardStats.totalLandSqft) * 100)
      : 0;

  return (
    <div className="md-dashboard-container">
      {/* Header */}
      <header className="md-dashboard-header">
        <div className="md-header-left">
          <img src={logo} className="md-logo" alt="" />
          <div className="md-topbar-title">
            <div className="md-project-name">{dashboardStats.projectName}</div>
            <div className="md-subtitle">
              Last updated:{" "}
              {dashboardStats.lastUpdated
                ? new Date(dashboardStats.lastUpdated).toLocaleString()
                : "—"}
            </div>
          </div>
        </div>

        <div className="md-header-actions">
          <button className="md-action-button" onClick={handleViewMap}>
            ViewMap
          </button>

          <button
            className="md-action-button md-refresh"
            onClick={fetchVillaData}
            disabled={isRefreshing}
            title="Refresh dashboard"
          >
            <FontAwesomeIcon icon={faRotateRight} style={{ marginRight: 8 }} />
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </button>

          <button
            className="md-action-button"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Logging Out..." : "LogOut"}
          </button>
        </div>
      </header>

      <div className="md-main-content">
        {/* Sidebar */}
        <div className="md-sidebar">
          <div
            className={`md-sidebar-item ${activeSection === "home" ? "md-active" : ""}`}
            onClick={() => setActiveSection("home")}
          >
            <FontAwesomeIcon icon={faHome} className="md-sidebar-icon" />
            Home
          </div>

          <div
            className={`md-sidebar-item ${activeSection === "villas" ? "md-active" : ""}`}
            onClick={() => setActiveSection("villas")}
          >
            <FontAwesomeIcon icon={faTree} className="md-sidebar-icon" />
            Inventory
          </div>

          <div
            className={`md-sidebar-item ${activeSection === "clients" ? "md-active" : ""}`}
            onClick={() => setActiveSection("clients")}
          >
            <FontAwesomeIcon icon={faUsers} className="md-sidebar-icon" />
            Clients
          </div>

          <div
            className={`md-sidebar-item ${activeSection === "bookings" ? "md-active" : ""}`}
            onClick={() => setActiveSection("bookings")}
          >
            <FontAwesomeIcon icon={faFileInvoice} className="md-sidebar-icon" />
            Bookings
          </div>

          <div
            className={`md-sidebar-item ${activeSection === "report" ? "md-active" : ""}`}
            onClick={() => setActiveSection("report")}
          >
            <FontAwesomeIcon icon={faChartBar} className="md-sidebar-icon" />
            Reports & Leads
          </div>

          <div className="md-sidebar-footer">Luminexa Technologies | Villa Management</div>
        </div>

        {/* Main Content */}
        <div className="md-content-area">
          {error && <div className="md-error-message">{error}</div>}

          {activeSection === "home" && (
            <div className="md-section-content">
              {/* Top summary strip */}
              <div className="md-home-top">
                <div>
                  <div className="md-home-title">Total Villas</div>
                  <div className="md-home-total">{dashboardStats.totalVillas}</div>
                </div>

                <div className="md-mini-stats">
                  <div className="md-mini-card">
                    <div className="md-mini-label">Sold %</div>
                    <div className="md-mini-value">{soldPct}%</div>
                  </div>
                  <div className="md-mini-card">
                    <div className="md-mini-label">Booked %</div>
                    <div className="md-mini-value">{bookedPct}%</div>
                  </div>
                  <div className="md-mini-card">
                    <div className="md-mini-label">Blocked %</div>
                    <div className="md-mini-value">{blockedPct}%</div>
                  </div>
                </div>
              </div>

              {/* KPI cards */}
              <div className="md-kpi-grid">
                <div className="md-kpi-card">
                  <div className="md-kpi-left">
                    <div className="md-kpi-label">Available Villas</div>
                    <div className="md-kpi-value">{dashboardStats.available}</div>
                    <div className="md-chip md-chip-green">Ready to book</div>
                  </div>
                  <div className="md-kpi-icon">
                    <FontAwesomeIcon icon={faHouse} />
                  </div>
                </div>

                <div className="md-kpi-card">
                  <div className="md-kpi-left">
                    <div className="md-kpi-label">Booked Villas</div>
                    <div className="md-kpi-value">{dashboardStats.booked}</div>
                    <div className="md-chip md-chip-yellow">In progress</div>
                  </div>
                  <div className="md-kpi-icon">
                    <FontAwesomeIcon icon={faHand} />
                  </div>
                </div>

                <div className="md-kpi-card">
                  <div className="md-kpi-left">
                    <div className="md-kpi-label">Sold Villas</div>
                    <div className="md-kpi-value">{dashboardStats.sold}</div>
                    <div className="md-chip md-chip-red">Closed deals</div>
                  </div>
                  <div className="md-kpi-icon">
                    <FontAwesomeIcon icon={faCircleCheck} />
                  </div>
                </div>

                <div className="md-kpi-card">
                  <div className="md-kpi-left">
                    <div className="md-kpi-label">Soft-Blocked</div>
                    <div className="md-kpi-value">{dashboardStats.blocked}</div>
                    <div className="md-chip md-chip-gray">On hold</div>
                  </div>
                  <div className="md-kpi-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                </div>
              </div>

              {/* Analytics panels (NO Revenue Snapshot) */}
              <div className="md-analytics-grid">
                {/* Left panel: Area Summary */}
                <div className="md-panel">
                  <div className="md-panel-title">Area Summary</div>

                  <div className="md-row">
                    <span>Total Built-up (sqft)</span>
                    <strong>{formatNum(dashboardStats.totalBuiltUpSqft)}</strong>
                  </div>

                  <div className="md-row">
                    <span>Total Land (sqft)</span>
                    <strong>{formatNum(dashboardStats.totalLandSqft)}</strong>
                  </div>

                  <div className="md-progress-wrap">
                    <div className="md-progress-label">
                      <span>Sold Progress</span>
                      <span>{soldPct}%</span>
                    </div>
                    <div className="md-progress-bar">
                      <div className="md-progress-fill" style={{ width: `${soldPct}%` }} />
                    </div>

                    <div className="md-progress-label" style={{ marginTop: 10 }}>
                      <span>Booked Progress</span>
                      <span>{bookedPct}%</span>
                    </div>
                    <div className="md-progress-bar">
                      <div
                        className="md-progress-fill md-progress-fill-amber"
                        style={{ width: `${bookedPct}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Right panel: Land vs Built-up */}
                <div className="md-panel">
                  <div className="md-panel-title">Land vs Built-up Area</div>

                  <div className="md-row">
                    <span>Total Built-up (sqft)</span>
                    <strong>{formatNum(dashboardStats.totalBuiltUpSqft)}</strong>
                  </div>

                  <div className="md-row">
                    <span>Total Land (sqft)</span>
                    <strong>{formatNum(dashboardStats.totalLandSqft)}</strong>
                  </div>

                  <div className="md-progress-wrap">
                    <div className="md-progress-label">
                      <span>Built-up vs Land</span>
                      <span>{builtUpVsLandRatio}%</span>
                    </div>
                    <div className="md-progress-bar">
                      <div
                        className="md-progress-fill"
                        style={{ width: `${builtUpVsLandRatio}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Insights */}
              <div className="md-insights-grid">
                <div className="md-panel">
                  <div className="md-panel-title">Top Configurations</div>
                  {topConfigs.length ? (
                    topConfigs.map(([k, v]) => (
                      <div className="md-row" key={k}>
                        <span>{k}</span>
                        <strong>{v}</strong>
                      </div>
                    ))
                  ) : (
                    <div className="md-muted">No configuration data found.</div>
                  )}
                </div>

                <div className="md-panel">
                  <div className="md-panel-title">Top Facings</div>
                  {topFacings.length ? (
                    topFacings.map(([k, v]) => (
                      <div className="md-row" key={k}>
                        <span title={k}>{k}</span>
                        <strong>{v}</strong>
                      </div>
                    ))
                  ) : (
                    <div className="md-muted">No facing data found.</div>
                  )}
                </div>
              </div>

              {/* Note */}
              <div className="md-note-box">
                <div className="md-note-title">Note and Instructions</div>
                <p className="md-note-text">
                  This dashboard provides complete villa management including inventory,
                  client details, bookings, leads, and reports. You can update villa status,
                  monitor bookings, and manage clients.
                </p>
              </div>
            </div>
          )}

          {activeSection === "villas" && (
            <PlotManagement
              setError={setError}
              refreshPlots={fetchVillaData}
              selectedProject={"ClarksExoticaVilla"}
            />
          )}

          {activeSection === "clients" && <Users setError={setError} />}
          {activeSection === "bookings" && <Bookings setError={setError} />}
          {activeSection === "report" && <Report setError={setError} />}
        </div>
      </div>
    </div>
  );
};

export default ManagementDashboard;
